/**
 * TanStack Query layer for the Segment Builder.
 *
 * - `useSegmentFields` — the org's sliceable fields + their FIXED (whole-portfolio)
 *   stats that drive the controls (bounds + value lists never shift as you filter).
 * - `useSegmentCount` — the live "N of total" headline. `keepPreviousData` holds the
 *   prior number while the next count is in flight (the panel shows a quiet "updating…").
 *   A count that failed with the typed SEGMENT_TOO_LARGE code (statement timeout on a
 *   pathological slice) is NOT auto-retried — it is deterministic; the panel offers a
 *   manual retry instead ({@link isSegmentTooLargeError}).
 * - `useSegmentSync` — binds the ACTIVE org into the (org-namespaced) segment store,
 *   hydrates the live store from the server draft, then debounce-autosaves edits back
 *   (cross-device), flushing any pending save on unmount. Hydration + autosave are both
 *   scoped to the bound org, so an org switch can never save one tenant's slice into
 *   another's draft.
 */
import { useEffect, useRef } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { apiClient } from '@repo/shared/client';
import { contractQueryOptions } from '~/lib/query';
import { useFlushOnUnmount } from '~/lib/hooks/use-flush-on-unmount';
import { currentOrganizationQueryOptions } from '~/lib/queries/organization';
import {
  AppError,
  SEGMENT_TOO_LARGE_CODE,
  portfolioSegmentFieldsContract,
  portfolioSegmentCountContract,
  portfolioSegmentContract,
  portfolioSegmentUpsertContract,
  portfolioSegmentFieldValuesContract,
  type SegmentFieldsResponse,
  type SegmentCountResponse,
  type SegmentDraftResponse,
  type SegmentFieldValuesResponse,
  type Segment,
} from '@repo/shared/common';
import { PORTFOLIO_KEY } from './use-portfolio';
import { useSegment, useSegmentActions, useSegmentStore } from '~/lib/stores/segment';

/** Debounce (ms) for the live count + autosave — calm, not chatty. */
const SEGMENT_DEBOUNCE_MS = 400;
const AUTOSAVE_DEBOUNCE_MS = 800;

export const segmentKeys = {
  fields: [...PORTFOLIO_KEY, 'segment-fields'] as const,
  count: (segment: Segment) => [...PORTFOLIO_KEY, 'segment-count', segment] as const,
  draft: [...PORTFOLIO_KEY, 'segment-draft'] as const,
  // F122 — the whole-portfolio value search for ONE truncated field, keyed on (field, query).
  fieldValues: (key: string, search: string) =>
    [...PORTFOLIO_KEY, 'segment-field-values', key, search] as const,
} as const;

/** The sliceable fields + their fixed stats (the panel's control source). */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- opaque queryOptions brand; the contract keeps callers typed.
export function segmentFieldsQueryOptions() {
  return contractQueryOptions(portfolioSegmentFieldsContract, [...segmentKeys.fields]);
}

export function useSegmentFields(): UseQueryResult<SegmentFieldsResponse> {
  return useQuery(segmentFieldsQueryOptions());
}

/** True when an error is the typed "segment too large" failure (count/list statement
 *  timeout) — deterministic, so callers skip auto-retry and show a targeted hint. */
export function isSegmentTooLargeError(error: unknown): boolean {
  return error instanceof AppError && error.code === SEGMENT_TOO_LARGE_CODE;
}

/**
 * The live count for a segment. `keepPreviousData` holds the last number while the next
 * is fetched, so the headline never flickers to a spinner — it just says "updating…".
 * On failure the panel keeps its last GOOD count (the caller carries it — see
 * PortfolioPageConnected) with a "couldn't update" hint + retry; the typed
 * segment-too-large failure is never auto-retried.
 */
export function useSegmentCount(segment: Segment): UseQueryResult<SegmentCountResponse> {
  return useQuery({
    queryKey: [...segmentKeys.count(segment)],
    queryFn: () => apiClient(portfolioSegmentCountContract, { body: { segment } }),
    placeholderData: keepPreviousData,
    retry: (failureCount, error) => !isSegmentTooLargeError(error) && failureCount < 2,
  });
}

/**
 * F122 — search the WHOLE distinct value set of ONE truncated field (a >200-distinct
 * categorical/text field whose stored top-200 can't reach the tail). `enabled` gates the
 * fetch to truncated fields only; `keepPreviousData` holds the last page while the next query
 * settles so the checklist doesn't flicker as the user types.
 */
export function useFieldValueSearch(
  key: string,
  search: string,
  enabled: boolean,
): UseQueryResult<SegmentFieldValuesResponse> {
  return useQuery({
    queryKey: [...segmentKeys.fieldValues(key, search)],
    queryFn: () =>
      apiClient(portfolioSegmentFieldValuesContract, { query: { key, search } }),
    enabled,
    placeholderData: keepPreviousData,
    // The distinct value set only moves on a data refresh; keep the search responsive.
    staleTime: 30_000,
  });
}

/** The per-user segment draft options, shared by the route loader and the sync hook. */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- opaque queryOptions brand; the contract keeps callers typed.
export function segmentDraftQueryOptions() {
  return contractQueryOptions(portfolioSegmentContract, [...segmentKeys.draft]);
}

function useSegmentDraft(): UseQueryResult<SegmentDraftResponse> {
  return useQuery(segmentDraftQueryOptions());
}

/**
 * The active segment as pages READ it. Wraps the store read with a first-paint
 * fallback: until `useSegmentSync` binds the active org, the (org-namespaced) store
 * deliberately reads EMPTY — so during that pre-bind window serve the user's saved
 * draft straight from the query cache instead. Both routes loader-await
 * {@link segmentDraftQueryOptions}, so the SSR render and the client's hydration
 * render read the identical dehydrated draft (no mismatch), and the org-bind +
 * draft-hydrate effects land as visual no-ops (`hydrate` stores the same object the
 * fallback served). Once the org is bound the store is authoritative, so org-switch
 * behavior is untouched; with no cached draft this is exactly the store read.
 */
export function useActiveSegment(): Segment {
  const stored = useSegment();
  const bound = useSegmentStore((state) => state.organizationId !== null);
  const queryClient = useQueryClient();
  if (bound) return stored;
  const draft = queryClient.getQueryData<SegmentDraftResponse>([...segmentKeys.draft]);
  return draft?.segment ?? stored;
}

function useUpsertSegmentDraft(): UseMutationResult<SegmentDraftResponse, Error, Segment> {
  return useMutation<SegmentDraftResponse, Error, Segment>({
    mutationFn: (segment) => apiClient(portfolioSegmentUpsertContract, { body: { segment } }),
  });
}

/**
 * Reconcile the live store with the server draft, PER ORGANIZATION:
 *
 * 1. Bind the active org id into the store (the store is namespaced by org — until the
 *    binding lands it reads empty, so another tenant's persisted draft never flashes).
 * 2. Hydrate ONCE PER ORG from the server draft (the cross-device source of truth) over
 *    the localStorage instant-paint value — but only from a draft response that is
 *    FRESH and not refetching, so an org switch can never hydrate the previous org's
 *    cached draft into the new org's slot (the switch invalidates the draft query; we
 *    wait out the refetch).
 * 3. Debounce-autosave subsequent edits, keyed to the org they were made under. A
 *    `lastSaved` guard prevents the hydration itself (or an unchanged value) from
 *    triggering a redundant write, and pending edits flush on unmount so navigating
 *    away never drops a change.
 */
export function useSegmentSync(): void {
  const orgQuery = useQuery(currentOrganizationQueryOptions());
  const organizationId = orgQuery.data?.organization?.id ?? null;

  const draft = useSegmentDraft();
  const upsert = useUpsertSegmentDraft();
  const segment = useSegment();
  const { hydrate, setOrganization } = useSegmentActions();

  // 1. Org binding — the store no-ops edits and reads empty until this lands.
  useEffect(() => {
    setOrganization(organizationId);
  }, [organizationId, setOrganization]);

  // Which org we last hydrated + last saved for; an org switch resets the cycle.
  const hydratedOrgRef = useRef<string | null>(null);
  const lastSavedRef = useRef<string | null>(null);
  const upsertRef = useRef(upsert);
  upsertRef.current = upsert;

  // 2. Per-org hydration, gated on a fresh, settled draft response (see JSDoc).
  useEffect(() => {
    if (organizationId === null || hydratedOrgRef.current === organizationId) return;
    if (!draft.data || draft.isFetching || draft.isStale) return;
    hydrate(draft.data.segment);
    lastSavedRef.current = JSON.stringify(draft.data.segment);
    hydratedOrgRef.current = organizationId;
  }, [organizationId, draft.data, draft.isFetching, draft.isStale, hydrate]);

  // 3. Autosave — only after this org hydrated, so a switch never writes cross-tenant.
  const [debounced] = useDebouncedValue(segment, AUTOSAVE_DEBOUNCE_MS);
  useEffect(() => {
    if (organizationId === null || hydratedOrgRef.current !== organizationId) return;
    const json = JSON.stringify(debounced);
    if (json === lastSavedRef.current) return;
    lastSavedRef.current = json;
    upsertRef.current.mutate(debounced);
  }, [debounced, organizationId]);

  // Flush a pending (un-debounced) edit when the user navigates away mid-window.
  useFlushOnUnmount(() => {
    if (organizationId === null || hydratedOrgRef.current !== organizationId) return;
    const json = JSON.stringify(segment);
    if (json === lastSavedRef.current) return;
    lastSavedRef.current = json;
    upsertRef.current.mutate(segment);
  });
}

export { SEGMENT_DEBOUNCE_MS };
